import React, { createContext, useState, useContext, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import authService from "../services/authService";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null); // { id, email, role, ... }
  const [loading, setLoading] = useState(true);
  const queryClient = useQueryClient();

  // The QueryClient outlives every session (it is created once, outside the
  // auth tree), and several keys — groupsMy, groupsPending, topRfps — are not
  // scoped by user. Without this, signing in as someone else in the same tab
  // serves the previous account's cached rows, and My Groups renders them as
  // "Owner". Wipe the cache on both edges of a session.
  const resetServerState = () => {
    queryClient.cancelQueries();
    queryClient.clear();
  };

  useEffect(() => {
    const verifyUser = async () => {
      try {
        const result = await authService.verify();
        if (result && result.success && result.data) {
          setUser(result.data);
          setIsAuthenticated(true);
        } else {
          setUser(null);
          setIsAuthenticated(false);
        }
      } catch (error) {
        console.error("Verification error:", error);
        setUser(null);
        setIsAuthenticated(false);
      } finally {
        setLoading(false);
      }
    };

    verifyUser();
  }, []);

  const login = async (email, password) => {
    try {
      const result = await authService.login(email, password);
      // Expecting shape: { success: true, data: { user, token } }
      const loggedInUser = result?.data?.user || null;
      resetServerState();
      if (loggedInUser) {
        setUser(loggedInUser);
        setIsAuthenticated(true);
        return loggedInUser; // allow caller to route by role
      }
      // Fallback if structure is unexpected
      setIsAuthenticated(true);
      return null;
    } catch (error) {
      setIsAuthenticated(false);
      setUser(null);
      // The error message is already formatted by authService
      throw error;
    }
  };

  const logout = async () => {
    try {
      await authService.logout();
    } catch (error) {
      console.error("Logout failed:", error);
      // Even if server logout fails, force logout on client
    } finally {
      setIsAuthenticated(false);
      setUser(null);
      resetServerState();
    }
  };

  const value = {
    isAuthenticated,
    user,
    loading,
    login,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  return useContext(AuthContext);
};
